import { useState } from "react";
import Sidebar from "../../components/feature/Sidebar";
import TopBar from "../../components/feature/TopBar";
import HubTaskPanel from "../../components/feature/HubTaskPanel";
import { ipTasks, ipCollabTasks } from "../../mocks/hubTasks";

const allIpTasks = [...ipTasks, ...ipCollabTasks];
import { useCompany } from "../../contexts/CompanyContext";

type Tab = "tasks" | "works" | "licensing" | "rights";

const works = [
  { title: "어둠 속의 빛", genre: "판타지", platform: "네이버웹툰", episodes: 87, readers: 1250000, status: "연재중", weekday: "화·금" },
  { title: "로맨스 클래스", genre: "로맨스", platform: "카카오웹툰", episodes: 145, readers: 3800000, status: "완결", weekday: "-" },
  { title: "레전드 오브 미라", genre: "액션", platform: "레진코믹스", episodes: 34, readers: 420000, status: "연재중", weekday: "수" },
  { title: "수상한 가족", genre: "일상", platform: "네이버웹툰", episodes: 210, readers: 5600000, status: "완결", weekday: "-" },
];

const licenses = [
  { work: "로맨스 클래스", type: "드라마 판권", partner: "SBS 드라마국", amount: 45000, status: "계약완료", territory: "국내" },
  { work: "수상한 가족", type: "게임 IP", partner: "넥슨 코리아", amount: 30000, status: "계약완료", territory: "국내" },
  { work: "어둠 속의 빛", type: "OTT 판권", partner: "Netflix Korea", amount: 80000, status: "협상중", territory: "글로벌" },
  { work: "로맨스 클래스", type: "해외 번역출판", partner: "日本 秋田書店", amount: 8000, status: "계약완료", territory: "일본" },
  { work: "수상한 가족", type: "굿즈 라이선싱", partner: "카카오프렌즈", amount: 12000, status: "계약완료", territory: "국내" },
];

const rights = [
  { work: "어둠 속의 빛", type: "저작권 등록", status: "등록완료", regNumber: "C-2024-001234", date: "2024.03.15" },
  { work: "로맨스 클래스", type: "저작권 등록", status: "등록완료", regNumber: "C-2023-008821", date: "2023.11.20" },
  { work: "수상한 가족", type: "저작권 등록", status: "등록완료", regNumber: "C-2022-015500", date: "2022.06.10" },
  { work: "레전드 오브 미라", type: "캐릭터 상표", status: "출원중", regNumber: "출원번호 대기", date: "2026.03.01" },
];

export default function IpHub() {
  const { workspaceId } = useCompany();
  const [activeTab, setActiveTab] = useState<Tab>("tasks");

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: "tasks", label: "내 할 일", icon: "ri-checkbox-multiple-line" },
    { id: "works", label: "연재 작품 관리", icon: "ri-quill-pen-line" },
    { id: "licensing", label: "IP 라이선싱", icon: "ri-copyright-line" },
    { id: "rights", label: "저작권 등록", icon: "ri-shield-check-line" },
  ];

  const totalLicenseRevenue = licenses.filter(l => l.status === "계약완료").reduce((s, l) => s + l.amount, 0);

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex font-sans">
      <Sidebar />
      <div className="flex-1 ml-56 flex flex-col">
        <TopBar />
        <main className="flex-1 p-5">
          {/* Header */}
          <div className="bg-[#111112] border border-white/[0.06] rounded-xl px-5 py-4 flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#4B6CF7]/10 border border-[#4B6CF7]/20">
                <i className="ri-copyright-line text-[#7A9BFF] text-lg"></i>
              </div>
              <div>
                <h1 className="text-[#F2F2F2] font-semibold text-sm">IP 스튜디오</h1>
                <p className="text-[#444444] text-xs">웹툰 연재 · IP 라이선싱 · 저작권 보호</p>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <div className="text-right">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest">연재 작품</div>
                <div className="text-[#F2F2F2] font-semibold text-base">{works.filter(w => w.status === "연재중").length}개</div>
              </div>
              <div className="text-right">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest">IP 라이선스 수익</div>
                <div className="text-[#888888] font-semibold text-base">{totalLicenseRevenue.toLocaleString()}만원</div>
              </div>
              <div className="text-right">
                <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest">총 독자</div>
                <div className="text-[#888888] font-semibold text-base">{(works.reduce((s, w) => s + w.readers, 0) / 10000).toFixed(0)}만명</div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-0.5 mb-5 bg-white/[0.03] rounded-xl p-1 w-fit border border-white/[0.05]">
            {tabs.map(tab => (
              <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id ? "bg-[#4B6CF7]/15 text-[#7A9BFF] border border-[#4B6CF7]/20" : "text-[#444444] hover:text-[#888888]"
                }`}>
                <i className={`${tab.icon} text-sm`}></i>{tab.label}
              </button>
            ))}
          </div>

          {activeTab === "tasks" && <HubTaskPanel tasks={allIpTasks} hubTitle="IP 담당" workspaceId={workspaceId} hub="ip" />}

          {activeTab === "works" && (
            <div className="grid grid-cols-2 gap-3">
              {works.map((w) => (
                <div key={w.title} className="bg-[#111112] border border-white/[0.06] rounded-xl p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-[#F2F2F2] text-sm font-semibold">{w.title}</div>
                      <div className="text-[#3D3D3D] text-xs mt-0.5">{w.genre} · {w.platform}</div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${
                      w.status === "연재중" ? "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20" : "text-[#444444] border-white/[0.05]"
                    }`}>{w.status}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-[#3D3D3D] text-[10px]">회차</div>
                      <div className="text-[#888888] text-sm font-medium">{w.episodes}화</div>
                    </div>
                    <div>
                      <div className="text-[#3D3D3D] text-[10px]">독자 수</div>
                      <div className="text-[#888888] text-sm font-medium">{(w.readers / 10000).toFixed(0)}만</div>
                    </div>
                    <div>
                      <div className="text-[#3D3D3D] text-[10px]">연재일</div>
                      <div className="text-[#888888] text-sm font-medium">{w.weekday}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "licensing" && (
            <div className="bg-[#111112] border border-white/[0.06] rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/[0.04]">
                    {["작품명", "라이선스 유형", "파트너", "금액", "지역", "상태"].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-[#3D3D3D] font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.03]">
                  {licenses.map((l, i) => (
                    <tr key={i} className="hover:bg-white/[0.02]">
                      <td className="px-5 py-3 text-[#CCCCCC] font-medium">{l.work}</td>
                      <td className="px-5 py-3 text-[#888888]">{l.type}</td>
                      <td className="px-5 py-3 text-[#555555]">{l.partner}</td>
                      <td className="px-5 py-3 text-[#888888] tabular-nums">{l.amount.toLocaleString()}만원</td>
                      <td className="px-5 py-3 text-[#444444]">{l.territory}</td>
                      <td className="px-5 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${
                          l.status === "계약완료" ? "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20" : "text-[#CCCCCC]/40 border-white/10"
                        }`}>{l.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "rights" && (
            <div className="bg-[#111112] border border-white/[0.06] rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/[0.04]">
                    {["작품명", "등록 유형", "등록번호", "등록일", "상태"].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-[#3D3D3D] font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.03]">
                  {rights.map((r, i) => (
                    <tr key={i} className="hover:bg-white/[0.02]">
                      <td className="px-5 py-3 text-[#CCCCCC] font-medium">{r.work}</td>
                      <td className="px-5 py-3 text-[#888888]">{r.type}</td>
                      <td className="px-5 py-3 text-[#444444] font-mono text-[10px]">{r.regNumber}</td>
                      <td className="px-5 py-3 text-[#444444]">{r.date}</td>
                      <td className="px-5 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${
                          r.status === "등록완료" ? "text-[#7A9BFF] bg-[#4B6CF7]/10 border-[#4B6CF7]/20" : "text-[#CCCCCC]/40 border-white/10"
                        }`}>{r.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
